import { getUiLocale } from '../../../../utils/uiTranslation';
import { systemMessage } from '../../../../utils/localizedSystemMessages';
import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const ApprovePTOModal = ({ employeeId, companyUID, employeeName, ptoRequests, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [action, setAction] = useState(null); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  const pendingRequests = Object.entries(ptoRequests || {})
    .filter(([_, req]) => req.status === 'pending')
    .map(([id, req]) => ({ id, ...req }));

  const handleApprove = async (requestId) => {
    setSelectedRequestId(requestId);
    setAction('approve');
    setComment('');
  };

  const handleReject = async (requestId) => {
    setSelectedRequestId(requestId);
    setAction('reject');
    setComment('');
  };

  const handleSubmit = async () => {
    if (!selectedRequestId || !action) return;

    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/approve-pto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          requestId: selectedRequestId,
          action, // 'approve' or 'reject'
          comment: comment.trim() || undefined,
          approvedByUID: currentUser?.uid,
          approvedByName: currentUser?.displayName || currentUser?.email || 'HR Team'
        })
      });

      const result = await response.json();
      if (result.success) {
        // Send Notification
        try {
          const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
          await set(notifRef, {
            ...systemMessage('pto_decision', { action, start: selectedRequest?.startDate || '', end: selectedRequest?.endDate || '', name: currentUser?.displayName || t('drawerModals.common.hrTeam', 'HR Team') }, getUiLocale()),
            type: action === 'approve' ? 'success' : 'error',
            timestamp: Date.now(),
            read: false
          });
        } catch (err) { console.error(err); }

        if (action === 'approve') {
          alert(t('drawerModals.approvePto.successApprove', 'PTO request approved successfully'));
        } else {
          alert(t('drawerModals.approvePto.successReject', 'PTO request rejected successfully'));
        }
        onSuccess?.();
        onClose();
      } else {
        alert((action === 'approve'
          ? t('drawerModals.approvePto.failureApprove', 'Failed to approve PTO request')
          : t('drawerModals.approvePto.failureReject', 'Failed to reject PTO request')
        ) + (result.error ? `: ${result.error}` : ''));
      }
    } catch (error) {
      console.error('Approve PTO failed:', error);
      alert(action === 'approve'
        ? t('drawerModals.approvePto.failureApprove', 'Failed to approve PTO request')
        : t('drawerModals.approvePto.failureReject', 'Failed to reject PTO request')
      );
    } finally {
      setProcessing(false);
      setSelectedRequestId(null);
      setAction(null);
      setComment('');
    }
  };

  if (pendingRequests.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '500px',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          <div className="modal-header">
            <h2>{t('drawerModals.approvePto.emptyTitle', 'Approve PTO')}</h2>
            <button className="close" onClick={onClose}>&times;</button>
          </div>
          <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
            <i className="fa-solid fa-check-circle" style={{ fontSize: '48px', color: '#568ded', marginBottom: '16px' }}></i>
            <div style={{ fontFamily: 'Montserrat', fontSize: '18px', color: 'var(--app-heading, #031128)', fontWeight: 600 }}>
              {t('drawerModals.approvePto.noPending', 'No pending PTO requests')}
            </div>
            <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#666', marginTop: '8px' }}>
              {t('drawerModals.approvePto.allProcessed', 'All PTO requests have been processed')}
            </div>
          </div>
          <div className="modal-footer" style={{
            padding: '20px 32px',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: isRTL ? 'row-reverse' : 'row'
          }}>
            <button
              className="btn-primary"
              onClick={onClose}
              style={{ '--light-control-fill': '#568ded',
                padding: '12px 24px',
                backgroundColor: '#568ded',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: 'pointer'
              }}
              data-light-control={"filled"}>
              {t('common:buttons.close', 'Close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedRequest = pendingRequests.find(req => req.id === selectedRequestId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '700px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.approvePto.title', 'Approve PTO Requests')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.approvePto.pendingCount', { count: pendingRequests.length, defaultValue: `Pending Requests (${pendingRequests.length})` })}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: '16px',
                    backgroundColor: selectedRequestId === req.id ? '#F0F7FF' : '#F8F9FA',
                    border: `2px solid ${selectedRequestId === req.id ? '#568ded' : '#E0E0E0'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => {
                    setSelectedRequestId(req.id);
                    setAction(null);
                    setComment('');
                  }}
                 >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: 'var(--app-heading, #031128)', marginBottom: '4px' }}>
                        {t(`pto.types.${req.type || 'vacation'}`, req.type || 'vacation')} {t('drawerModals.approvePto.leaveLabel', 'Leave')}
                      </div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#666' }}>
                        {t('drawerModals.approvePto.range', { start: req.startDate, end: req.endDate, defaultValue: `${req.startDate} to ${req.endDate}` })} • {t('pto.requestDays', { count: req.days || 0, defaultValue: `${req.days || 0} days` })}
                      </div>
                      {req.comment && (
                        <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                          "{req.comment}"
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(req.id);
                        }}
                        disabled={processing}
                        style={{ '--light-control-fill': '#40EB6E',
                          padding: '6px 12px',
                          backgroundColor: '#40EB6E',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: processing ? 'not-allowed' : 'pointer'
                        }}
                        data-light-control={"filled"}>
                        {t('drawerModals.approvePto.approve', 'Approve')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(req.id);
                        }}
                        disabled={processing}
                        style={{ '--light-control-fill': '#EB4040',
                          padding: '6px 12px',
                          backgroundColor: '#EB4040',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: processing ? 'not-allowed' : 'pointer'
                        }}
                        data-light-control={"filled"}>
                        {t('drawerModals.approvePto.reject', 'Reject')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedRequest && action && (
            <div className="input-group" style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: action === 'approve' ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${action === 'approve' ? '#40EB6E' : '#EB4040'}`,
              borderRadius: '8px'
            }}>
              <div style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: 'var(--app-heading, #031128)', marginBottom: '8px' }}>
                {action === 'approve' ? t('drawerModals.approvePto.approveRequest', 'Approve Request') : t('drawerModals.approvePto.rejectRequest', 'Reject Request')}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                {t('drawerModals.approvePto.range', { start: selectedRequest.startDate, end: selectedRequest.endDate, defaultValue: `${selectedRequest.startDate} to ${selectedRequest.endDate}` })} • {t('pto.requestDays', { count: selectedRequest.days || 0, defaultValue: `${selectedRequest.days || 0} days` })}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('drawerModals.approvePto.commentPlaceholder', 'Add a comment (optional)...')}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px 16px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  color: 'var(--app-heading, #031128)',
                  resize: 'vertical',
                  marginBottom: '12px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setSelectedRequestId(null);
                    setAction(null);
                    setComment('');
                  }}
                  disabled={processing}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--app-heading, #031128)',
                    cursor: processing ? 'not-allowed' : 'pointer'
                  }}
                  data-light-control={"quiet"}>
                  {t('common:buttons.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={processing}
                  style={{ '--light-control-fill': action === 'approve' ? '#40EB6E' : '#EB4040',
                    padding: '8px 16px',
                    backgroundColor: action === 'approve' ? '#40EB6E' : '#EB4040',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: action === 'approve' ? "#FFFFFF" : "#FFFFFF",
                    cursor: processing ? 'not-allowed' : 'pointer'
                  }}
                  data-light-control={action === 'approve' ? "filled" : "filled"}>
                  {processing
                    ? t('drawerModals.approvePto.processing', 'Processing...')
                    : (action === 'approve'
                      ? t('drawerModals.approvePto.approveRequest', 'Approve Request')
                      : t('drawerModals.approvePto.rejectRequest', 'Reject Request'))}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={processing}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovePTOModal;
